import {ApiErrorKeys, ClassicWorkflowStatusColorKeyToNextMap, NextGroupLevelId, NextWorkflowStatusColorKeyToClassicMap} from './constants'
import _, { isNumber } from 'underscore'
import { collectCustomFieldValues } from '../customFields/utils';
import {WorkflowStatusType} from '../workflowStatus/constants'
import {stringComparator} from '../atUtils'

export function isNextApiError(error) {
    return error.name && error.name === 'ApiError'
}

export function isNetworkError(error) {
    return error.name && error.name === 'NetworkError'
}

export function isNotFoundError(error, entityType) {
    const body = error.body
    return body.errorKey === ApiErrorKeys.NotFoundEntity && (!entityType || body.entityType === entityType)
}

export function isUnauthenticated(error) {
    const body = error.body
    return body.errorKey === ApiErrorKeys.Unauthenticated
}

export function mergePreviousWorkflowStatuses(previousWorkflowStatuses, workflowStatuses) {
    return Object.entries(workflowStatuses).reduce((acc, [id, workflowStatus]) => {
        const previousWorkflowStatus = previousWorkflowStatuses[id]
        if (!previousWorkflowStatus || previousWorkflowStatus.nextId === undefined) return { ...acc, [id]: workflowStatus}
        return { ...acc, [id]: { ...workflowStatus, nextId: previousWorkflowStatus.nextId }}
    }, {})
}

export function mergeClassicAndNextTask(classicTask, nextTask) {
    return {
        ...classicTask,
        nextId: nextTask.id,
        customFieldsValues: collectCustomFieldValues(nextTask),
        spentTime: nextTask.spentTime ? nextDurationToMinutes(nextTask.spentTime) : null,
        variance: convertVariance(nextTask.variance),
        progress: convertProgress(nextTask.progress)
    }
}

export function nextDurationToMinutes(durationStr) {
    const reptms = /^PT(?:(-?\d+)H)?(?:(-?\d+)M)?$/
    let hours = 0, minutes = 0, seconds = 0, totalMinutes = 0

    if (reptms.test(durationStr)) {
        const matches = reptms.exec(durationStr)
        if (matches[1]) hours = Number(matches[1])
        if (matches[2]) minutes = Number(matches[2])
        totalMinutes = hours * 60 + minutes
    }
    return totalMinutes
}

export function convertVariance(variance) {
    return variance
        ? {
            duration: variance.duration ? nextDurationToMinutes(variance.duration) : null,
            percentage: isNumber(variance.percentage) ? variance.percentage * 100 : null
        }
        : null
}

function convertProgress(progress) {
    return isNumber(progress) ? progress : null
}

export function mergeRpcAndNextTaskData(rpcTasks, nextData) {
    const nextValuesDataMap = {}
    nextData.items.forEach(nextTask => nextValuesDataMap[nextTask.classicId] = nextTask)

    return rpcTasks.map((classicTask) => {
        const nextTask = nextValuesDataMap[classicTask.id]
        if (nextTask)
            return mergeClassicAndNextTask(classicTask, nextTask)

        return classicTask
    })
}

export function convertClassicWorkflowStatusToNext(classicWorkflowStatus) {
    return {
        id: classicWorkflowStatus.nextId,
        classicId: classicWorkflowStatus.id,
        name: classicWorkflowStatus.name,
        position: classicWorkflowStatus.position,
        targetActive: classicWorkflowStatus.type === WorkflowStatusType.OPEN,
        colorKey: ClassicWorkflowStatusColorKeyToNextMap[classicWorkflowStatus.colorId]
    }
}

export function convertNextWorkflowStatusToClassic(nextWorkflowStatus) {
    return {
        id: nextWorkflowStatus.classicId,
        nextId: nextWorkflowStatus.id,
        name: nextWorkflowStatus.name,
        position: nextWorkflowStatus.position,
        type: nextWorkflowStatus.targetActive ? WorkflowStatusType.OPEN : WorkflowStatusType.COMPLETED,
        colorId: NextWorkflowStatusColorKeyToClassicMap[nextWorkflowStatus.colorKey] || 0,
    }
}

export function convertNextWorkflowStatusesToClassic(nextWorkflowStatuses) {
    return nextWorkflowStatuses.reduce((acc, nextWorkflowStatus) => {
        return { ...acc, [nextWorkflowStatus.classicId]: convertNextWorkflowStatusToClassic(nextWorkflowStatus)}
    }, {})
}

export function convertNextTargetGroupToClassic(nextTargetGroup, nextParentTargetGroups = []) {
    const nextParentTargetGroup = nextParentTargetGroups.find((nextParentTargetGroup) => nextTargetGroup.parentId === nextParentTargetGroup.id)

    return {
        id: nextTargetGroup.classicId,
        name: nextTargetGroup.title,
        archived: !nextTargetGroup.active,
        customerId: nextParentTargetGroup && nextParentTargetGroup.classicId,
    }
}

export function convertNextKanbanSummaryToClassicKanbanInitial(nextKanbanSummary) {
    const referencedNextTargetGroups = nextKanbanSummary.referenced.targetGroups ? Object.values(nextKanbanSummary.referenced.targetGroups) : []
    let nextProjects = referencedNextTargetGroups
        .filter((nextTargetGroup) => nextTargetGroup.levelId === NextGroupLevelId.Project)
    const nextCustomers= referencedNextTargetGroups
        .filter((nextTargetGroup) => nextTargetGroup.levelId === NextGroupLevelId.Customer)
        .sort((a, b) => stringComparator(a.title, b.title))
    const nextWorkflowStatuses = nextKanbanSummary.referenced.workflowStatuses ? Object.values(nextKanbanSummary.referenced.workflowStatuses) : []

    nextProjects = nextCustomers.reduce((acc, nextCustomer) => {
        const groupedNextProject = nextProjects.filter((project) => project.parentId === nextCustomer.id)
        return [...acc, ...groupedNextProject.sort((a, b) => stringComparator(a.title, b.title))]
    }, [])

    const classicWorkflowStatuses = nextWorkflowStatuses.map((nextWorkflowStatus) => convertNextWorkflowStatusToClassic(nextWorkflowStatus))

    nextKanbanSummary.items = nextKanbanSummary.items.map((item) => {
        const classicWorkflowStatus = classicWorkflowStatuses.find((workflowStatus) => workflowStatus.nextId === item.workflowStatusId)
        return {
            ...item,
            classicId: classicWorkflowStatus.id,
        }
    })

    const taskCountByStatusId = nextKanbanSummary.items.reduce((acc, curr) => ({
        ...acc,
        [curr.classicId]: curr.targetsCount,
    }), {})

    return {
        taskCountByStatusId: taskCountByStatusId,
        workflowStatuses: classicWorkflowStatuses,
        customersProjectsInfo: {
            projects: nextProjects.map((nextProject) => convertNextTargetGroupToClassic(nextProject, nextCustomers)),
            customers: nextCustomers.map((nextCustomer) => convertNextTargetGroupToClassic(nextCustomer), []),
            projectIdsContainingTasks: nextProjects.map((nextProject) => nextProject.classicId)
        },
    }

}


// WEBPACK FOOTER //
// ./web/js/next/utils.js